import { useCallback, useEffect, useRef, useState } from 'react';
import { readString, writeString } from '../../lib/storage';
import {
  constrainLine,
  computedColorToHex,
  floodFill,
  hexToRgb,
  rectFrom,
  rgbToHex,
  sprayOffset,
  stampSteve,
  type Size,
} from './drawing';
import { usesPointer, type Tool } from './tools';

/** Profondeur de la pile d'annulation (handoff § Edition). */
export const HISTORY_DEPTH = 12;

/** Clé du calque conservé d'une visite à l'autre, et sa taille maximale. */
const LAYER_KEY = 'steve.paint.layer';
const LAYER_MAX_BYTES = 2_000_000;
const SAVE_DEBOUNCE_MS = 800;

/** Aérographe : points par image, et rayon du disque semé. */
const SPRAY_PER_FRAME = 20;
const SPRAY_RADIUS = 15;
const SPRAY_ALPHA = 0.6;

/** Le tampon grossit avec la taille de trait choisie. */
const STAMP_SCALE: Record<Size, number> = { 1: 0.18, 3: 0.28, 6: 0.42, 10: 0.62 };

/** Police du texte gravé dans le calque. */
export const TEXT_FONT_SIZE = 18;
const TEXT_FONT = `${String(TEXT_FONT_SIZE)}px 'Patrick Hand', cursive`;

export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Rect extends Point {
  readonly w: number;
  readonly h: number;
}

interface Config {
  readonly tool: Tool;
  readonly primary: string;
  /** Couleur de remplissage des formes ; `null` = contour seul. */
  readonly secondary: string | null;
  readonly size: Size;
  /** La pipette renvoie sa prise. */
  readonly onPick: (hex: string) => void;
  /** L'outil texte demande un champ de saisie à cet endroit. */
  readonly onTextPlace: (point: Point) => void;
  /** Coordonnées et taille de forme, écrites directement dans la barre d'état. */
  readonly onCoords: (point: Point | null) => void;
  readonly onShape: (rect: Rect | null) => void;
  readonly onSelection: (rect: Rect | null) => void;
  /** Vrai quand le visiteur demande moins d'animation : la marquise ne défile pas. */
  readonly reducedMotion: boolean;
}

export interface PaintLayer {
  readonly areaRef: React.RefObject<HTMLDivElement | null>;
  readonly layerRef: React.RefObject<HTMLCanvasElement | null>;
  readonly overlayRef: React.RefObject<HTMLCanvasElement | null>;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly dirty: boolean;
  readonly undo: () => void;
  readonly redo: () => void;
  readonly clear: () => void;
  readonly commitText: (text: string, at: Point) => void;
  readonly eraseSelection: () => void;
  readonly drawImageFile: (file: File) => Promise<boolean>;
  readonly onPointerDown: (event: React.PointerEvent<HTMLCanvasElement>) => void;
  readonly onPointerMove: (event: React.PointerEvent<HTMLCanvasElement>) => void;
  readonly onPointerUp: (event: React.PointerEvent<HTMLCanvasElement>) => void;
}

/**
 * Densité de pixels du calque.
 *
 * La toile du skin paint fait toute la hauteur du contenu : sur une page longue
 * et un écran à forte densité, un calque à `devicePixelRatio` plein dépasserait
 * la dizaine de millions de pixels — de la mémoire pour rien, et un risque de
 * canvas vide selon le navigateur. On plafonne, quitte à dessiner un peu moins
 * finement sur les très grandes pages.
 */
export function pixelRatioFor(width: number, height: number, deviceRatio = 1): number {
  const capped = Math.min(Math.max(deviceRatio, 1), 2);
  const area = Math.max(1, width * height);
  const maxPixels = 6_000_000;
  return Math.max(1, Math.min(capped, Math.sqrt(maxPixels / area)));
}

interface Snapshot {
  readonly url: string;
  readonly width: number;
  readonly height: number;
}

/**
 * Le contexte du calque, annoncé en lecture fréquente.
 *
 * Le calque est **relu après chaque trait** : `toDataURL` pour la sauvegarde
 * différée, `getImageData` pour la pipette et pour le remplissage. Sans cette
 * option, le navigateur garde le canvas en mémoire graphique et redescend les
 * pixels à chaque lecture — c'est le « GPU stall » qu'il finit par signaler
 * lui-même en console. Avec elle, le canvas vit côté processeur : les lectures
 * ne coûtent plus rien, et le tracé n'y perd rien de mesurable à cette taille.
 *
 * L'option n'est lue qu'au **premier** appel pour un canvas donné ; les appels
 * suivants la signalent en vain. D'où ce passage obligé, plutôt qu'un
 * `getContext` écrit à la main à chaque endroit.
 */
function layerContext(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  return canvas.getContext('2d', { willReadFrequently: true });
}

export function usePaintLayer(config: Config): PaintLayer {
  const areaRef = useRef<HTMLDivElement | null>(null);
  const layerRef = useRef<HTMLCanvasElement | null>(null);
  const overlayRef = useRef<HTMLCanvasElement | null>(null);

  // La configuration change à chaque frappe de couleur ou d'outil : les
  // gestionnaires de pointeur la lisent par référence pour ne jamais se
  // ré-abonner en plein tracé.
  const configRef = useRef(config);
  configRef.current = config;

  const cssSize = useRef({ width: 0, height: 0 });
  const past = useRef<Snapshot[]>([]);
  const future = useRef<Snapshot[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [dirty, setDirty] = useState(false);
  const saveTimer = useRef<number | null>(null);

  const stroke = useRef<{
    pointerId: number;
    tool: Tool;
    from: Point;
    last: Point;
    shift: boolean;
  } | null>(null);
  const selection = useRef<Rect | null>(null);
  const marquee = useRef(0);

  const context = useCallback(
    (): CanvasRenderingContext2D | null =>
      layerRef.current ? layerContext(layerRef.current) : null,
    [],
  );

  // ── Sauvegarde différée ─────────────────────────────────────────────────
  const scheduleSave = useCallback((): void => {
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      saveTimer.current = null;
      const layer = layerRef.current;
      if (!layer) return;
      try {
        const url = layer.toDataURL('image/png');
        // Un gribouillage démesuré ne doit pas remplir le stockage du visiteur :
        // dans ce cas on préfère ne rien garder plutôt que d'échouer bruyamment.
        if (url.length > LAYER_MAX_BYTES) {
          writeString(LAYER_KEY, '');
          return;
        }
        writeString(LAYER_KEY, url);
      } catch {
        // Canvas trop grand, stockage refusé : le dessin reste à l'écran.
      }
    }, SAVE_DEBOUNCE_MS);
  }, []);

  /** Empile l'état courant du calque (appelé avant chaque modification validée). */
  const pushHistory = useCallback((): void => {
    const layer = layerRef.current;
    if (!layer) return;
    try {
      past.current = [
        ...past.current.slice(-(HISTORY_DEPTH - 1)),
        {
          url: layer.toDataURL('image/png'),
          width: cssSize.current.width,
          height: cssSize.current.height,
        },
      ];
      future.current = [];
      setCanUndo(true);
      setCanRedo(false);
    } catch {
      // Rien à empiler : l'annulation sautera ce pas, le dessin reste juste.
    }
  }, []);

  const paintSnapshot = useCallback((snapshot: Snapshot | undefined): void => {
    const ctx = context();
    if (!ctx) return;
    const { width, height } = cssSize.current;
    ctx.clearRect(0, 0, width, height);
    if (!snapshot) return;
    const image = new Image();
    image.onload = () => {
      ctx.drawImage(image, 0, 0, snapshot.width, snapshot.height);
    };
    image.src = snapshot.url;
  }, [context]);

  const undo = useCallback((): void => {
    const layer = layerRef.current;
    const previous = past.current.at(-1);
    if (!layer || !previous) return;
    future.current = [
      ...future.current.slice(-(HISTORY_DEPTH - 1)),
      {
        url: layer.toDataURL('image/png'),
        width: cssSize.current.width,
        height: cssSize.current.height,
      },
    ];
    past.current = past.current.slice(0, -1);
    paintSnapshot(previous);
    setCanUndo(past.current.length > 0);
    setCanRedo(true);
    scheduleSave();
  }, [paintSnapshot, scheduleSave]);

  const redo = useCallback((): void => {
    const layer = layerRef.current;
    const next = future.current.at(-1);
    if (!layer || !next) return;
    past.current = [
      ...past.current.slice(-(HISTORY_DEPTH - 1)),
      {
        url: layer.toDataURL('image/png'),
        width: cssSize.current.width,
        height: cssSize.current.height,
      },
    ];
    future.current = future.current.slice(0, -1);
    paintSnapshot(next);
    setCanUndo(true);
    setCanRedo(future.current.length > 0);
    scheduleSave();
  }, [paintSnapshot, scheduleSave]);

  const clear = useCallback((): void => {
    pushHistory();
    const ctx = context();
    if (ctx) ctx.clearRect(0, 0, cssSize.current.width, cssSize.current.height);
    setDirty(false);
    scheduleSave();
  }, [context, pushHistory, scheduleSave]);

  // ── Dimensionnement, en préservant le dessin ────────────────────────────
  useEffect(() => {
    const area = areaRef.current;
    const layer = layerRef.current;
    const overlay = overlayRef.current;
    if (!area || !layer || !overlay) return;

    const applySize = (): void => {
      const width = area.clientWidth;
      const height = area.clientHeight;
      if (width === 0 || height === 0) return;
      if (width === cssSize.current.width && height === cssSize.current.height) return;

      const ratio = pixelRatioFor(width, height, window.devicePixelRatio);
      const previous = document.createElement('canvas');
      previous.width = layer.width;
      previous.height = layer.height;
      const keep = previous.getContext('2d');
      if (keep && layer.width > 0) keep.drawImage(layer, 0, 0);
      const before = { ...cssSize.current };

      for (const canvas of [layer, overlay]) {
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
        // Seul le calque est relu ; l'aperçu, lui, n'est que dessiné.
        const ctx = canvas === layer ? layerContext(canvas) : canvas.getContext('2d');
        ctx?.scale(ratio, ratio);
      }
      cssSize.current = { width, height };

      const ctx = layerContext(layer);
      // Redessiné aux dimensions CSS d'avant : le trait garde sa place sur la
      // page, il ne repart jamais de zéro sur un redimensionnement.
      if (ctx && before.width > 0) ctx.drawImage(previous, 0, 0, before.width, before.height);
    };

    applySize();

    // Restauration de la visite précédente, une seule fois, après le premier
    // dimensionnement — sinon l'image serait posée sur un calque de 0 × 0.
    const stored = readString(LAYER_KEY, LAYER_MAX_BYTES);
    if (stored && stored.startsWith('data:image/png')) {
      const image = new Image();
      image.onload = () => {
        const ctx = layerContext(layer);
        if (!ctx) return;
        ctx.drawImage(image, 0, 0, cssSize.current.width, cssSize.current.height);
        setDirty(true);
      };
      image.src = stored;
    }

    // `ResizeObserver` manque dans quelques environnements très anciens (et
    // sous jsdom) : le calque garde alors sa taille de départ, le reste marche.
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(applySize);
    observer?.observe(area);
    return () => {
      observer?.disconnect();
    };
  }, []);

  // ── Marquise de sélection ───────────────────────────────────────────────
  const drawOverlay = useCallback(
    (preview?: (ctx: CanvasRenderingContext2D) => void): void => {
      const overlay = overlayRef.current;
      const ctx = overlay?.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, cssSize.current.width, cssSize.current.height);

      const rect = selection.current;
      if (rect) {
        ctx.save();
        ctx.setLineDash([5, 3]);
        ctx.lineDashOffset = -marquee.current;
        ctx.lineWidth = 1;
        ctx.strokeStyle = '#16130f';
        ctx.strokeRect(rect.x + 0.5, rect.y + 0.5, rect.w, rect.h);
        ctx.restore();
      }
      preview?.(ctx);
    },
    [],
  );

  useEffect(() => {
    if (config.reducedMotion) return;
    let frame = 0;
    const step = (): void => {
      if (selection.current) {
        marquee.current = (marquee.current + 1) % 8;
        drawOverlay();
      }
      frame = window.requestAnimationFrame(step);
    };
    frame = window.requestAnimationFrame(step);
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [config.reducedMotion, drawOverlay]);

  /*
   * Quitter la sélection décroche son rectangle.
   *
   * Sans ça, les pointillés continuent de courir sur la toile alors que l'outil
   * qui les gère n'est plus actif — et `Suppr` effacerait une zone que le
   * visiteur croit avoir abandonnée.
   */
  useEffect(() => {
    if (config.tool === 'select' || selection.current === null) return;
    selection.current = null;
    configRef.current.onSelection(null);
    drawOverlay();
  }, [config.tool, drawOverlay]);

  // ── Outils ──────────────────────────────────────────────────────────────
  const pointFrom = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const applyStrokeStyle = (ctx: CanvasRenderingContext2D, tool: Tool): void => {
    const { primary, size } = configRef.current;
    ctx.globalCompositeOperation = tool === 'eraser' ? 'destination-out' : 'source-over';
    ctx.globalAlpha = 1;
    ctx.strokeStyle = primary;
    ctx.fillStyle = primary;
    ctx.lineWidth = tool === 'eraser' ? size * 3 : size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  /** Couleur sous un point : le calque d'abord, le site en dessous ensuite. */
  const pickAt = (event: React.PointerEvent<HTMLCanvasElement>, point: Point): string | null => {
    const ctx = context();
    if (!ctx) return null;
    try {
      const ratio = layerRef.current
        ? layerRef.current.width / Math.max(1, cssSize.current.width)
        : 1;
      const pixel = ctx.getImageData(
        Math.round(point.x * ratio),
        Math.round(point.y * ratio),
        1,
        1,
      ).data;
      if ((pixel[3] ?? 0) > 0) {
        return rgbToHex({ r: pixel[0] ?? 0, g: pixel[1] ?? 0, b: pixel[2] ?? 0 });
      }
    } catch {
      // Calque illisible : on tombe sur la couleur du site.
    }

    const overlay = overlayRef.current;
    const previous = overlay?.style.pointerEvents ?? '';
    if (overlay) overlay.style.pointerEvents = 'none';
    let element = document.elementFromPoint(event.clientX, event.clientY);
    if (overlay) overlay.style.pointerEvents = previous;
    while (element) {
      const color = computedColorToHex(window.getComputedStyle(element).backgroundColor);
      if (color) return color;
      element = element.parentElement;
    }
    return null;
  };

  const commitText = useCallback(
    (text: string, at: Point): void => {
      const trimmed = text.trim();
      if (trimmed === '') return;
      const ctx = context();
      if (!ctx) return;
      pushHistory();
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      ctx.fillStyle = configRef.current.primary;
      ctx.font = TEXT_FONT;
      ctx.textBaseline = 'top';
      ctx.fillText(trimmed, at.x, at.y);
      setDirty(true);
      scheduleSave();
    },
    [context, pushHistory, scheduleSave],
  );

  const eraseSelection = useCallback((): void => {
    const rect = selection.current;
    const ctx = context();
    if (!rect || !ctx || rect.w === 0 || rect.h === 0) return;
    pushHistory();
    ctx.clearRect(rect.x, rect.y, rect.w, rect.h);
    selection.current = null;
    configRef.current.onSelection(null);
    drawOverlay();
    scheduleSave();
  }, [context, drawOverlay, pushHistory, scheduleSave]);

  const drawImageFile = useCallback(
    async (file: File): Promise<boolean> => {
      const ctx = context();
      if (!ctx) return false;
      const url = URL.createObjectURL(file);
      try {
        const image = await new Promise<HTMLImageElement>((resolve, reject) => {
          const element = new Image();
          element.onload = () => {
            resolve(element);
          };
          element.onerror = () => {
            reject(new Error('image illisible'));
          };
          element.src = url;
        });
        pushHistory();
        const { width, height } = cssSize.current;
        // Contenue dans la toile, centrée, jamais agrandie au-delà du cadre.
        const scale = Math.min(width / image.width, height / image.height, 1);
        const w = image.width * scale;
        const h = image.height * scale;
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 1;
        ctx.drawImage(image, (width - w) / 2, (height - h) / 2, w, h);
        setDirty(true);
        scheduleSave();
        return true;
      } catch {
        return false;
      } finally {
        URL.revokeObjectURL(url);
      }
    },
    [context, pushHistory, scheduleSave],
  );

  const onPointerDown = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const { tool } = configRef.current;
    if (!usesPointer(tool)) return;
    // Un second doigt annule le tracé en cours : c'est le geste de rattrapage.
    if (stroke.current && stroke.current.pointerId !== event.pointerId) {
      stroke.current = null;
      drawOverlay();
      return;
    }
    const ctx = context();
    if (!ctx) return;
    const point = pointFrom(event);
    // Sans ça, le clic de compatibilité qui suit déplace le focus hors du champ
    // que l'outil texte vient de poser — et la frappe part dans la page.
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);

    if (tool === 'picker') {
      const color = pickAt(event, point);
      if (color) configRef.current.onPick(color);
      return;
    }

    if (tool === 'text') {
      configRef.current.onTextPlace(point);
      return;
    }

    if (tool === 'fill') {
      const layer = layerRef.current;
      if (!layer) return;
      try {
        const image = ctx.getImageData(0, 0, layer.width, layer.height);
        const ratio = layer.width / Math.max(1, cssSize.current.width);
        const painted = floodFill(
          image,
          point.x * ratio,
          point.y * ratio,
          hexToRgb(configRef.current.primary),
        );
        if (painted > 0) {
          pushHistory();
          ctx.save();
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.putImageData(image, 0, 0);
          ctx.restore();
          setDirty(true);
          scheduleSave();
        }
      } catch {
        // Calque illisible : rien de peint, rien de cassé.
      }
      return;
    }

    if (tool === 'stamp') {
      pushHistory();
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      stampSteve(
        ctx,
        point.x,
        point.y,
        STAMP_SCALE[configRef.current.size],
        ((Math.random() * 24 - 12) * Math.PI) / 180,
      );
      setDirty(true);
      scheduleSave();
      return;
    }

    stroke.current = { pointerId: event.pointerId, tool, from: point, last: point, shift: event.shiftKey };

    if (tool === 'select') return;

    if (tool === 'pencil' || tool === 'eraser' || tool === 'spray') {
      pushHistory();
      applyStrokeStyle(ctx, tool);
      if (tool === 'spray') {
        sprayAt(ctx, point);
      } else {
        ctx.beginPath();
        ctx.moveTo(point.x, point.y);
        ctx.lineTo(point.x, point.y);
        ctx.stroke();
      }
      setDirty(true);
    } else {
      // Formes : l'aperçu vit sur la couche du dessus, le calque n'est touché
      // qu'au relâchement.
      pushHistory();
    }
  };

  const sprayAt = (ctx: CanvasRenderingContext2D, point: Point): void => {
    ctx.save();
    ctx.globalAlpha = SPRAY_ALPHA;
    ctx.fillStyle = configRef.current.primary;
    for (let index = 0; index < SPRAY_PER_FRAME; index += 1) {
      const offset = sprayOffset(SPRAY_RADIUS);
      ctx.fillRect(point.x + offset.x, point.y + offset.y, 1, 1);
    }
    ctx.restore();
  };

  const previewShape = (ctx: CanvasRenderingContext2D, from: Point, to: Point, shift: boolean, tool: Tool): void => {
    const { primary, secondary, size } = configRef.current;
    ctx.save();
    ctx.strokeStyle = primary;
    ctx.lineWidth = size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (tool === 'line') {
      const end = shift ? constrainLine(from.x, from.y, to.x, to.y) : to;
      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(end.x, end.y);
      ctx.stroke();
    } else {
      const rect = rectFrom(from.x, from.y, to.x, to.y, shift);
      ctx.beginPath();
      if (tool === 'rect') ctx.rect(rect.x, rect.y, rect.w, rect.h);
      else ctx.ellipse(rect.x + rect.w / 2, rect.y + rect.h / 2, rect.w / 2, rect.h / 2, 0, 0, Math.PI * 2);
      if (secondary) {
        ctx.fillStyle = secondary;
        ctx.fill();
      }
      ctx.stroke();
    }
    ctx.restore();
  };

  const onPointerMove = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const point = pointFrom(event);
    configRef.current.onCoords(point);

    const current = stroke.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const ctx = context();
    if (!ctx) return;
    current.shift = event.shiftKey;

    if (current.tool === 'pencil' || current.tool === 'eraser') {
      applyStrokeStyle(ctx, current.tool);
      ctx.beginPath();
      ctx.moveTo(current.last.x, current.last.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
      current.last = point;
      return;
    }

    if (current.tool === 'spray') {
      applyStrokeStyle(ctx, current.tool);
      sprayAt(ctx, point);
      current.last = point;
      return;
    }

    if (current.tool === 'select') {
      const rect = rectFrom(current.from.x, current.from.y, point.x, point.y, false);
      selection.current = rect;
      configRef.current.onShape(rect);
      drawOverlay();
      return;
    }

    const rect = rectFrom(current.from.x, current.from.y, point.x, point.y, current.shift);
    configRef.current.onShape(rect);
    drawOverlay((overlay) => {
      previewShape(overlay, current.from, point, current.shift, current.tool);
    });
  };

  const onPointerUp = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const current = stroke.current;
    if (!current || current.pointerId !== event.pointerId) return;
    stroke.current = null;
    const ctx = context();
    const point = pointFrom(event);

    if (ctx && (current.tool === 'rect' || current.tool === 'ellipse' || current.tool === 'line')) {
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
      previewShape(ctx, current.from, point, current.shift, current.tool);
      setDirty(true);
    }
    if (current.tool === 'select') {
      configRef.current.onSelection(selection.current);
    }
    if (current.tool !== 'select') {
      drawOverlay();
      scheduleSave();
    }
  };

  // ── Ménage ──────────────────────────────────────────────────────────────
  useEffect(
    () => () => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    },
    [],
  );

  return {
    areaRef,
    layerRef,
    overlayRef,
    canUndo,
    canRedo,
    dirty,
    undo,
    redo,
    clear,
    commitText,
    eraseSelection,
    drawImageFile,
    onPointerDown,
    onPointerMove,
    onPointerUp,
  };
}
